import { MobilePdpSummary } from "./ProductSummary/MobilePdpSummary";
import { IncludedPartsSection } from "./Housing/IncludedPartsSection";
import { OptionalPartsSection } from "./Housing/OptionalPartsSection";
import { isKITProduct } from "../../models/ProductData";
import { QuantityChangedSnackbar } from "./Housing/HousingQtyChangedSnackbar";
import { type ProductDetailsPageProps } from "./DesktopProductDetailsPage";
import { useTranslation } from "react-i18next";
import { PdpScanResultSection } from "~/ScanDMC/components/PdpScanResultSection/PdpScanResultSection";
import { usePdpTabs } from "./usePDPTabs";
import { Tabs } from "~/common/m3/Tabs/Tabs";
import { Tab } from "~/common/m3/Tabs/Tab";
import { Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";

export const MobileProductDetailsPage = (props: ProductDetailsPageProps) => {
  const productDescriptor = props.productDescriptor;
  const product = productDescriptor.base;

  const { t } = useTranslation();

  const { tabs, onTabActivationHandler, activeTabIndex } = usePdpTabs(productDescriptor);
  const { tabVariant, tabsRef, tabRefs } = useDynamicTabVariant();

  const { preferredProducts } = props;
  const hasPreferredProducts = (preferredProducts.data?.preferred.length ?? 0) > 0;

  return (
    <div className={"scha__pdp"}>
      <Stack gap={2}>
        <PdpScanResultSection className={"scha__pdp__dmc-section"} productCode={product.code} />
        <MobilePdpSummary
          productDescriptor={productDescriptor}
          hasPreferredProducts={hasPreferredProducts}
          changeTab={onTabActivationHandler}
        />
        {props.postSummarySlot}
        {isKITProduct(product) && (
          <div className={"scha__pdp__housing-elements-section"}>
            <QuantityChangedSnackbar />
            {(product.includedParts?.length ?? 0) > 0 && (
              <IncludedPartsSection housingProducts={product.includedParts} />
            )}
            {(product.optionalParts?.length ?? 0) > 0 && (
              <OptionalPartsSection housingProducts={product.optionalParts} />
            )}
          </div>
        )}

        {props.preferredProductsBlock}

        <Tabs
          id="tabs"
          onChange={(e, val) => {
            onTabActivationHandler(val);
          }}
          value={activeTabIndex}
          variant={tabVariant}
          fullWidthIndicator={tabVariant === "fullWidth" ? false : undefined}
          ref={tabsRef}
          scrollButtons={false}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={t(tab.title)}
              data-test-id={tab.testId}
              ref={(el) => (tabRefs.current[index] = el)}
            />
          ))}
        </Tabs>
        {tabs.map((tab) => (
          <div key={tab.value} hidden={activeTabIndex !== tab.value}>
            {tab.component}
          </div>
        ))}
      </Stack>
    </div>
  );
};

type TabVariant = "scrollable" | "fullWidth";

const useDynamicTabVariant = () => {
  const [tabVariant, setTabVariant] = useState<TabVariant>("scrollable");
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const tabsElement = tabsRef.current;
    if (!tabsElement) {
      return;
    }

    const calculateVariant = () => {
      const tabsWidth = tabsElement.clientWidth;
      let totalTabsWidth = tabRefs.current.reduce((total, tabRef) => {
        return total + (tabRef?.getBoundingClientRect().width ?? 0);
      }, 0);
      // truncate decimal places - fix for fractions of pixels
      totalTabsWidth = Math.trunc(totalTabsWidth);

      setTabVariant(totalTabsWidth > tabsWidth ? "scrollable" : "fullWidth");
    };

    const observer = new MutationObserver(calculateVariant);
    observer.observe(tabsElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    calculateVariant();

    return () => {
      observer.disconnect();
    };
  }, []);

  return {
    tabVariant,
    tabsRef,
    tabRefs,
  };
};
